import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import mainAxios from '../../services/mainAxios';
import CompanyAttributes from '../Companies/CompanyAttributes';
import PurchaseNotifications from '../Companies/PurchaseNotifications';
import SalesNotifications from '../Companies/SalesNotifications';
import Loader from '../Layout/Loader';
import SettingsTabs from './SettingsTabs';
import useSettingsCompany from './useSettingsCompany';
import SettingsBreadcrumb from './SettingsBreadcrumb';
import './settings.scss';

const PreferencesSettings = () => {
  const { t } = useTranslation('translations');
  const {
    company,
    setCompany,
    getCompany,
    loading,
    isAdminMode,
    getBackUrl,
    user,
    selectedCompany,
  } = useSettingsCompany();

  const [pageLoading, setPageLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const editCompany = ({ newCompany } = {}) => {
    return mainAxios
      .post('apiEndpoint/saveOrUpdate', {
        object: 'Company',
        content: newCompany ?? {
          ...company,
        },
        nuis: user.companiesRights.find(
          (c) => c.id === parseInt(selectedCompany)
        )?.nuis,
      })
      .then((res) => {
        if (res?.data?.status === true) {
          toast.success(t('company.parameterSaved'), {
            containerId: 'all_toast',
          });
          return true;
        }
        return false;
      })
      .catch(() => {
        return false;
      });
  };

  const handleCompanyInputs = (e, selectKey, selectValue) => {
    const key = e ? e.target.name : selectKey;
    const value = e ? e.target.value : selectValue;
    let content = { ...company, [key]: value };
    setCompany(content);
    return content;
  };

  const userRoleId = user?.role?.id;

  if (loading || pageLoading || !company) {
    return <Loader />;
  }

  return (
    <div className="settings-subpage">
      <SettingsBreadcrumb
        items={[{ label: t('settings.preferences') }]}
        company={company}
        isAdminMode={isAdminMode}
      />

      <div className="settings-subpage__header">
        <Link to={getBackUrl()} className="settings-subpage__back-btn">
          <FontAwesomeIcon icon={faArrowLeft} />
        </Link>
        <h2 className="settings-subpage__title">
          <span className="settings-subpage__icon">
            <FontAwesomeIcon icon={faCog} />
          </span>
          {t('settings.preferences')}
        </h2>
        {company && (
          <span className="settings-subpage__company-badge">
            {company.name}
          </span>
        )}
      </div>

      <SettingsTabs
        variant="card"
        showSearch={true}
        searchPlaceholder={t('common.search') + '...'}
        onSearch={setSearchQuery}
        tabs={[
          {
            id: 'parameters',
            label: t('company.parameters'),
            content: (
              <CompanyAttributes
                company={company}
                getCompany={getCompany}
                setCompany={setCompany}
                handleCompanyInputs={handleCompanyInputs}
                editCompany={editCompany}
                searchQuery={searchQuery}
              />
            ),
          },
          userRoleId === 1 && {
            id: 'advanced',
            label: t('company.advanSettings'),
            content: (
              <CompanyAttributes
                company={company}
                isAdvanced={true}
                getCompany={getCompany}
                setCompany={setCompany}
                handleCompanyInputs={handleCompanyInputs}
                editCompany={editCompany}
                searchQuery={searchQuery}
              />
            ),
          },
          {
            id: 'purchaseNotifications',
            label: t('company.purchaseNotifications'),
            content: <PurchaseNotifications companyId={company?.id} />,
          },
          {
            id: 'salesNotifications',
            label: t('company.salesNotifications'),
            content: <SalesNotifications companyId={company?.id} />,
          },
        ]}
      />
    </div>
  );
};

export default PreferencesSettings;
